import React, { useState } from 'react';
import {
  Box,
  Stepper as MStepper,
  StepLabel,
  Step,
  StepContent,
  Button,
  Typography,
} from '@mui/material';
import { COMMON_PAGE_TEXT } from '../lang';

type StepperProps = {
  activeStep: number;
  width?: number;
  onChange: (newStep: number) => void;
  steps: Steps[];
  hideButton?: boolean;
  clickable?: boolean;
};

export type Steps = {
  label: string;
  description?: string;
  hasDone?: boolean;
  clickableStep?: boolean;
};

export const Stepper: React.FunctionComponent<StepperProps> = props => {
  const {
    onChange,
    steps,
    clickable = true,
    activeStep: initActiveStep,
    hideButton = false,
    width = 180,
  } = props;
  const [activeStep, setActiveStep] = useState(initActiveStep);

  React.useEffect(() => {
    setActiveStep(initActiveStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [initActiveStep]);

  const handleNext = (): void => {
    onChange(activeStep + 1);
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleStep = (step: number) => () => {
    if (clickable) {
      if (steps[step].clickableStep === false) return;
      setActiveStep(step);
      onChange(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ width }}>
      <MStepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => {
          const { label, description, hasDone } = step;
          return (
            <Step key={label}>
              <StepLabel
                optional={
                  index === steps.length - 1 ? (
                    <Typography variant="caption">
                      {COMMON_PAGE_TEXT.BUTTON.LAST_STEP}
                    </Typography>
                  ) : null
                }
                icon={
                  <div
                    style={{
                      width: '1.5rem',
                      height: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#ccc',
                      color: '#fff',
                      borderRadius: '50%',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      ...(index === activeStep
                        ? {
                            fontWeight: 'bold',
                            backgroundColor: '#951D1D',
                          }
                        : {}),
                      ...(index !== activeStep && hasDone
                        ? { backgroundColor: '#D5A3A3' }
                        : {}),
                    }}
                  >
                    {String.fromCharCode(65 + index)[0]}
                  </div>
                }
                onClick={handleStep(index)}
              >
                {label}
              </StepLabel>
              <StepContent>
                <Typography fontSize={11} color={'gray'} mb={2} gutterBottom>
                  {description}
                </Typography>
                {clickable && (
                  <Box>
                    <div>
                      {index !== steps.length - 1 && !hideButton && (
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          size="small"
                          sx={{ textTransform: 'none' }}
                        >
                          {COMMON_PAGE_TEXT.BUTTON.CONTINUE}
                        </Button>
                      )}
                    </div>
                  </Box>
                )}
              </StepContent>
            </Step>
          );
        })}
      </MStepper>
    </Box>
  );
};
